import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import { useMediaQuery } from 'react-responsive';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import {
  generateUrlFromOfficeSearchOption,
  OfficeSearchOption,
} from '../../common/helpers/office.utils';
import useI18n from '../../common/hooks/useI18n';

import { OfficesSearchBar } from '.';

type Props = {
  autoCompleteValue: string;
  autoCompleteOptions: OfficeSearchOption[];
  groupedByCity: boolean;
  onAutoCompleteValueChange: (value: string) => void;
  imageUrl?: string;
  i18nKeyPrefix?: string;
  theme: DefaultTheme;
};

const titleKey = 'title';
const subtitleKey = 'subtitle';
const searchButtonKey = 'searchButton';

const Background = styled.div`
  height: 55vh;
  height: calc(var(--vh-resizable, 1vh) * 55);
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  @media ${(props) => props.theme.breakpoints.xld} {
    height: 80vh;
    height: calc(var(--vh-resizable, 1vh) * 80);
  }
  @media ${(props) => props.theme.breakpoints.mdd} {
    flex-direction: column;
    justify-content: center;
  }
`;

const ImageAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Title = styled.div`
  margin-left: ${(props) => props.theme.grid.getGridColumns(1, 1)};
  margin-right: ${(props) => props.theme.grid.getGridColumns(1, 1)};
  max-width: 40vw;
  background-color: rgba(242, 242, 242, 0.6);
  border-radius: ${(props) => props.theme.borderRadius};
  padding: 32px;
  box-shadow: 4px 4px 25px rgba(0, 0, 0, 0.15);

  @media ${(props) => props.theme.breakpoints.xld} {
    margin-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
    margin-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  }
  @media ${(props) => props.theme.breakpoints.lgd} {
    padding: 24px;
  }
  @media ${(props) => props.theme.breakpoints.mdd} {
    max-width: unset;
  }
`;

const TitleParagraph = styled.h1`
  ${(props) => props.theme.font.h2}
  color: ${(props) => props.theme.colors.secondary};
  line-height: 125% !important;
`;

const Divider = styled.hr`
  width: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-top: 24px;
  margin-bottom: 24px;
  margin-left: var(--gutter);
  margin-right: 0;
  border: 1px solid ${(props) => props.theme.colors.primary};
  background-color: ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius};
  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: 18px;
    margin-bottom: 18px;
  }
`;

const Subtitle = styled.h2`
  color: ${(props) => props.theme.colors.secondary};
  font-size: 18px;
  line-height: 27px;
  font-weight: 500;

  @media ${(props) => props.theme.breakpoints.lgd} {
    line-height: 22px;
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    font-size: 14px;
  }
`;

const FloatingArea = styled.div`
  position: absolute;
  height: 44px;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto auto;
  @media ${(props) => props.theme.breakpoints.smd} {
    width: ${(props) => props.theme.grid.getGridColumns(20, 0)};
    bottom: 40px;
  }
  @media ${(props) => props.theme.breakpoints.mdu} {
    -ms-transform: translateY(50%);
    transform: translateY(50%);
  }
  @media ${(props) => props.theme.breakpoints.sm} {
    width: ${(props) => props.theme.grid.getGridColumns(20, 0)};
  }
  @media ${(props) => props.theme.breakpoints.md} {
    width: ${(props) => props.theme.grid.getGridColumns(18, 0)};
  }
  @media ${(props) => props.theme.breakpoints.lg} {
    width: ${(props) => props.theme.grid.getGridColumns(16, 0)};
  }
  @media ${(props) => props.theme.breakpoints.xl} {
    width: ${(props) => props.theme.grid.getGridColumns(14, 0)};
  }
  @media ${(props) => props.theme.breakpoints.xxl} {
    width: ${(props) => props.theme.grid.getGridColumns(12, 0)};
  }
`;

const BuyYourHouseComponent = styled.div<{ $openTextBar: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 66.67%;
  z-index: 200;

  @media ${(props) => props.theme.breakpoints.mdu} {
    min-width: 256px;
    transform: ${(props) =>
      props.$openTextBar ? 'translateX(0)' : 'translateX(80px)'};
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    right: ${(props) => (props.$openTextBar ? '100%' : '0')};
    transition: right 0.4s ease-out;
    -webkit-transition: right 0.4s ease-out;
    overflow: hidden;
  }
`;

const SearchBarContainer = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 1%;
  right: 1%;
  z-index: 150;
  @media ${(props) => props.theme.breakpoints.mdu} {
    right: ${(props) => (props.$open ? '1%' : '100%')};
    transition: right 0.4s ease-out, transform 0.4s ease-out;
    -webkit-transition: right 0.4s ease-out, transform 0.4s ease-out;
    transform: ${(props) =>
      props.$open ? 'translateX(0)' : 'translateX(80px)'};
  }
`;

const ActionButton = styled(Button)<{ $themeColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  width: 100%;
  height: 100%;
  font-weight: 500;
  font-size: 15px;
  line-height: 100%;
  color: white;
  background-color: ${(props) => props.theme.colors[props.$themeColor]};
  border-color: ${(props) => props.theme.colors[props.$themeColor]};
  border-radius: 22px;
  padding: 0 30px;

  &:hover,
  &:focus,
  &:active {
    color: white;
    background-color: ${(props) => props.theme.colors[props.$themeColor]};
    border-color: ${(props) => props.theme.colors[props.$themeColor]};
  }
`;

const OfficesSearchHero: React.FC<Props> = ({
  autoCompleteValue,
  autoCompleteOptions,
  groupedByCity,
  onAutoCompleteValueChange,
  imageUrl,
  i18nKeyPrefix,
  theme,
}) => {
  const router = useRouter();
  const i18n = useI18n();

  const isMdu = !useMediaQuery({ query: theme.breakpoints.smd });

  const autoCompleteRef = useRef(null);

  const [searchBarOpen, setSearchBarOpen] = useState(isMdu);
  const [searchBarOpening, setSearchBarOpening] = useState(false);
  const [searchBarClosing, setSearchBarClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const title = useMemo(
    () =>
      i18n.t(`${i18nKeyPrefix}${titleKey}`) || i18n.t(`offices.${titleKey}`),
    [i18n, i18nKeyPrefix]
  );
  const subtitle = useMemo(
    () =>
      i18n.t(`${i18nKeyPrefix}${subtitleKey}`) ||
      i18n.t(`offices.${subtitleKey}`),
    [i18n, i18nKeyPrefix]
  );

  const onSelect = useCallback(
    (option: OfficeSearchOption) => {
      router.push({
        pathname: generateUrlFromOfficeSearchOption(option),
      });
    },
    [router]
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!searchBarOpen) {
      onAutoCompleteValueChange('');
    }
  }, [onAutoCompleteValueChange, searchBarOpen]);

  return (
    <Background>
      <ImageAbsoluteContainer>
        <ImageContainer>
          {isMounted && (
            <Image
              alt="Home hero"
              layout="fill"
              objectFit="cover"
              objectPosition="center center"
              src={imageUrl || '/images/banner_hero.jpg'}
              sizes={theme.getImageSizes('100vw')}
              priority
            />
          )}
        </ImageContainer>
      </ImageAbsoluteContainer>
      <Title>
        <TitleParagraph
          key={title.substring(0, 10) + new Date().getTime()}
          as="h1"
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
        <Divider />
        <Subtitle
          key={subtitle.substring(0, 10) + new Date().getTime()}
          as="h2"
          dangerouslySetInnerHTML={{
            __html: subtitle,
          }}
        />
      </Title>
      {isMounted && (
        <FloatingArea>
          <div
            onClick={() => {
              if (autoCompleteRef.current) {
                autoCompleteRef.current.focus();
                if (
                  !isMdu &&
                  !searchBarOpen &&
                  !searchBarOpening &&
                  !searchBarClosing
                ) {
                  setSearchBarOpening(true);
                  setTimeout(() => {
                    setSearchBarOpen(true);
                    setSearchBarOpening(false);
                  }, 300);
                }
              }
            }}
            onBlur={() => {
              if (
                !isMdu &&
                searchBarOpen &&
                !searchBarOpening &&
                !searchBarClosing
              ) {
                setSearchBarClosing(true);
                setTimeout(() => {
                  setSearchBarOpen(false);
                  setSearchBarClosing(false);
                }, 300);
              }
            }}
          >
            <SearchBarContainer as="div" $open={searchBarOpen}>
              <OfficesSearchBar
                height="44px"
                value={autoCompleteValue}
                options={autoCompleteOptions}
                groupdedByCity={groupedByCity}
                onValueChange={onAutoCompleteValueChange}
                buttonBackgroundColor={theme.colors.secondary}
                inputPadding={
                  isMdu && searchBarOpen ? 'calc(max(256px, 36%))' : undefined
                }
                ref={autoCompleteRef}
                onSelect={onSelect}
              />
            </SearchBarContainer>
            <BuyYourHouseComponent as="div" $openTextBar={searchBarOpen}>
              <ActionButton $themeColor="secondary">
                <span>
                  {i18n.t(`${i18nKeyPrefix}${searchButtonKey}`) ||
                    i18n.t(`offices.${searchButtonKey}`)}
                </span>
                {!isMdu && <SearchOutlined style={{ marginLeft: '8px' }} />}
              </ActionButton>
            </BuyYourHouseComponent>
          </div>
        </FloatingArea>
      )}
    </Background>
  );
};

export default withTheme<typeof OfficesSearchHero>(OfficesSearchHero);
